/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getViewPortHeight } from "selectors/documentViewSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { getDocumentVisibleHeight } from "selectors/documentViewSelectors";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  FILENAME_TOAST_HIDE_TIMEOUT,
  FILENAME_TOAST_ACTIVATION_REGION,
  HUD_HIDE_TIMEOUT,
  FLYOUT_HIDE_TIMEOUT,
  BOTTOM_PANEL_HIDE_TIMEOUT
} from "constants/constants";
import { fileNameToastReadyPromise, initialPageLoadedPromise } from "utils/DocumentViewUtils";
import * as stores from "stores";
import { getPanelVisiblity } from "selectors/bottomBoardSelectors";

class ComponentHideManager {
  constructor() {
    this.timeoutID = null;
  }

  setTimeoutID(timeoutID) {
    this.timeoutID = timeoutID;
  }

  resurfaceComponent(promise, autoHideCallback, timeoutValue) {
    promise(stores)
      .then(() => {
        if (this.timeoutID) {
          clearTimeout(this.timeoutID);
        }
        autoHideCallback(false);
        let id = window.setTimeout(() => {
          autoHideCallback(true);
        }, timeoutValue);
        this.setTimeoutID(id);
      })
      .catch(() => {});
  }

  clearTimeout() {
    clearTimeout(this.timeoutID);
  }

  reset() {
    this.timeoutID = null;
  }

  handleMouseMove() {
    console.error("should not call the base class function");
  }
}

class HudHideManager extends ComponentHideManager {
  constructor() {
    super();
  }

  handleMouseMove(stores) {
    this.resurfaceHud(stores);
  }

  handleMouseOverHud(stores, handle, evt) {
    if (!isCurrentDeviceMobile() && !isCurrentDeviceTablet()) {
      let rect = handle.getBoundingClientRect();
      // get relative mouse position
      let mouseX = evt.clientX - rect.left;
      let mouseY = evt.clientY - rect.top;
      if (mouseX > 0 && mouseX < handle.clientWidth && mouseY > 0 && mouseY < handle.clientHeight) {
        // mouse is over the hud
        stores.hudStore.setMouseOver(true);
      } else {
        stores.hudStore.setMouseOver(false);
      }
    }
  }

  resurfaceHud(stores) {
    super.resurfaceComponent(initialPageLoadedPromise, stores.hudStore.setShouldAutoHide, HUD_HIDE_TIMEOUT);
  }
}

class ToastHideManager extends ComponentHideManager {
  constructor() {
    super();
  }

  handleMouseMove(stores, handle, evt) {
    let mouseY = evt.clientY - handle.offsetTop; // get relative mouse position
    let docViewHeight = getViewPortHeight(stores);

    if (getIsInPlaceMode(stores)) {
      docViewHeight = getDocumentVisibleHeight(stores);
    }
    // activation region is 20% of visible document height
    docViewHeight = Math.floor(FILENAME_TOAST_ACTIVATION_REGION * docViewHeight);
    if (mouseY <= docViewHeight) {
      this.resurfaceToast(stores);
    }
  }

  resurfaceToast(stores) {
    super.resurfaceComponent(
      fileNameToastReadyPromise,
      stores.documentStore.setShouldAutoHideToast,
      FILENAME_TOAST_HIDE_TIMEOUT
    );
  }
}

class FlyoutHideManager extends ComponentHideManager {
  constructor() {
    super();
    this.setShouldAutoHide = null;
    this.setIsMouseOverFlyout = null;
    this.hideTimeout = null;
  }

  init({ setShouldAutoHide, setIsMouseOverFlyout, hideTimeout, shouldAutoHideInitial = true }) {
    this.setShouldAutoHide = setShouldAutoHide;
    this.setIsMouseOverFlyout = setIsMouseOverFlyout;
    this.hideTimeout = hideTimeout;
    if (shouldAutoHideInitial) {
      this.resurfaceFlyout();
    }
  }

  getIsMouseOverFlyout(handle, evt) {
    let rect = handle.getBoundingClientRect();
    let mouseX = evt.clientX;
    let mouseY = evt.clientY;
    return (
      mouseX > 0 &&
      mouseX >= rect.left &&
      mouseX <= rect.right &&
      mouseY > 0 &&
      mouseY >= rect.top &&
      mouseY <= rect.bottom
    );
  }

  handleMouseMove(handle, evt) {
    const isMouseOverFlyout = this.getIsMouseOverFlyout(handle, evt);
    this.setIsMouseOverFlyout(isMouseOverFlyout);
    if (isMouseOverFlyout) {
      this.resurfaceFlyout();
    }
  }

  resurfaceFlyout() {
    super.resurfaceComponent(
      () => Promise.resolve(),
      shouldAutoHide => this.setShouldAutoHide(shouldAutoHide),
      this.hideTimeout || FLYOUT_HIDE_TIMEOUT
    );
  }

  resetFlyoutHideManager() {
    this.setShouldAutoHide = null;
    this.setIsMouseOverFlyout = null;
  }
}

class BottomBoardHideManager extends ComponentHideManager {
  constructor() {
    super();
    this.isHiddenOnce = false;
  }

  handleMouseMove(stores, handle, evt) {
    let rect = handle.getBoundingClientRect();
    let mouseYRelativeToBottom = rect.height - evt.clientY + rect.top;
    if (mouseYRelativeToBottom > 110) {
      // 110 is height of invisible div
      this.resurfaceBoard(stores);
    } else {
      // if mouse is over the bottom board, clear the previous timeout
      if (this.timeoutID) {
        this.clearTimeout();
      }
    }
  }

  _handleAutoHideCallback(shouldHide) {
    if (shouldHide) {
      stores.bottomBoard.setPanelVisibility(false);
      if (!this.isHiddenOnce) {
        this.isHiddenOnce = true;
      }
    }
  }

  resurfaceBoard() {
    const timeout = this.isHiddenOnce ? BOTTOM_PANEL_HIDE_TIMEOUT : 2 * BOTTOM_PANEL_HIDE_TIMEOUT;
    super.resurfaceComponent(initialPageLoadedPromise, this._handleAutoHideCallback.bind(this), timeout);
  }
}

const hudHideManager = new HudHideManager();
const toastHideManager = new ToastHideManager();
const flyoutHideManager = new FlyoutHideManager();
const bottomBoardHideManager = new BottomBoardHideManager();

export { hudHideManager, toastHideManager, flyoutHideManager, bottomBoardHideManager };
